import React, { useRef, useState } from "react";
import Config from "../utils/Config";
import "./FFLExpirationVerification.css";

export default function FFLExpirationVerification({ claim, onSaved }) {
    const [date, setDate] = useState("");
    const [confirmed, setConfirmed] = useState(false);
    const [source, setSource] = useState("uploaded_license");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const lock = useRef(false);
    const stored = claim.expirationDate;
    const hasDate = typeof stored === "string" && stored.trim() && Number.isFinite(+new Date(stored));
    const type = String(claim.fflNumber || "").replace(/-/g, "").slice(6, 8);
    const ezCheckAvailable = !["03", "06"].includes(type);
    const save = async () => {
        if (lock.current || !date || !confirmed) return;
        lock.current = true; setBusy(true); setError("");
        try {
            const response = await fetch(`${Config.API.API_BASE_URL}/api/admin/claims/${encodeURIComponent(claim._id)}/expiration-date`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("authToken")}` },
                body: JSON.stringify({ expirationDate: date, confirmed: true, source }),
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.message || "Unable to save the verified expiration date. Refresh before retrying.");
            if (data.claimId !== String(claim._id) || data.expirationDate !== `${date}T00:00:00.000Z`) throw new Error("The saved date could not be confirmed. Refresh the claim.");
            onSaved(data.expirationDate);
        } catch (err) { setError(err.message || "Unable to save the verified expiration date."); }
        finally { lock.current = false; setBusy(false); }
    };
    return <section className="ffl-expiration-review" aria-label="FFL expiration verification">
        <strong>{hasDate ? "FFL expiration date on file" : "Verify FFL expiration date"}</strong>
        {hasDate ? <p>{new Date(stored).toISOString().slice(0, 10)} — existing date preserved.</p> : <>
            <p>Enter the exact expiration date printed on the uploaded FFL license. Do not infer a day from the FFL number. Save the verified date before approving this claim.</p>
            {ezCheckAvailable ? <a href="https://fflezcheck.atf.gov/FFLEzCheck/" target="_blank" rel="noopener noreferrer">Check the license using official ATF eZ Check</a> : <p>ATF eZ Check does not validate Type 03 or Type 06 licenses. Review the original license using the applicable manual verification process.</p>}
            <fieldset disabled={busy}>
                <label>Exact FFL expiration date<input type="date" value={date} onChange={e => setDate(e.target.value)} /></label>
                <label>Verification source<select value={source} onChange={e => setSource(e.target.value)}>
                    <option value="uploaded_license">Uploaded FFL license</option>
                    {ezCheckAvailable && <option value="uploaded_license_and_ezcheck">Uploaded FFL license and ATF eZ Check</option>}
                </select></label>
                <label className="ffl-expiration-confirm"><input type="checkbox" checked={confirmed} onChange={e => setConfirmed(e.target.checked)} /> I verified this exact date against the uploaded FFL license.</label>
                <button type="button" className="claim-ffl-button view-ffl-button-hollow" disabled={!date || !confirmed || busy} onClick={save}>{busy ? "Saving…" : "Save Verified Expiration Date"}</button>
            </fieldset>
        </>}
        {error && <p role="alert">{error}</p>}
    </section>;
}
